import { useMemo } from "react";
import { NAVS, TabKey } from "./customerInsightsConstants";
import { InfoTooltip } from "../Components";
import { formatNumber } from "../utils/format-utils";
import { Neutral200, Neutral600, Success700 } from "../utils/colors";
import ProgressBar from "@ramonak/react-progress-bar";

interface CustomerInsightsHeaderProps {
  tab: TabKey;
  matchRate?: { matchedPopulation: number; totalPopulation: number; fraction: number };
}

const CustomerInsightsHeader: React.FC<CustomerInsightsHeaderProps> = ({ tab, matchRate }) => {
  const { matchedPopulation, totalPopulation, fraction } = matchRate ?? {
    matchedPopulation: 0,
    totalPopulation: 0,
    fraction: 0,
  };

  const prettyLabel = useMemo(() => {
    return NAVS.find(nav => nav.key === tab)?.label ?? "";
  }, [tab]);

  const aboutCopy = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return "Customer Base describes the composition of your customers by observable characteristics such as their age, household income, place of residence, etc.";
      case TabKey.INDEX:
        return "Index shows the relative percentage of your customer base belonging to each group, as compared to the percentage of the total U.S. market belonging to that group.";
      case TabKey.PENETRATION:
        return "Penetration shows the percentage of the total U.S. market that is represented in your customer base for each group.";
    }
  }, [tab]);

  return (
    <div className="customerInsightsHeader">
      <div className="headerItem about">
        <div className="headerTitle">About {prettyLabel}</div>
        <div className="headerBody">{aboutCopy}</div>
      </div>
      <div className="headerItem emailMatchRate">
        <div className="headerTitle">
          Email Match Rate
          <InfoTooltip size="reg" textColor={Neutral600}>
            This is the percentage of distinct customer email addresses that have been successfully
            recognized by the TransUnion graph, which contains a little over 800 million U.S. email
            addresses in total. For large lists of consumer email addresses, resolution rates
            typically hover around 50% - if your rate is much below 50%, it is a good idea to review
            hashing protocol. Your Tinuiti team can share specifics, but all emails should have
            white spaces and special characters removed, they should be lower-cased, and the SHA-256
            hashing algorithm should be applied.
          </InfoTooltip>
        </div>
        <div className="headerBody">
          <div className="progressBarLabel">
            <div className="lineItem">
              <span>{formatNumber(totalPopulation)} unique emails ingested</span>
              <InfoTooltip size="sm" textColor={Neutral600}>
                This is the full set of customer email addresses observed at least once within the
                past twelve months.
              </InfoTooltip>
            </div>
            <div className="lineItem">{formatNumber(matchedPopulation)} emails recognized</div>
          </div>
          <div className="progressBarContainer">
            <ProgressBar
              baseBgColor={Neutral200}
              bgColor={Success700}
              borderRadius="8px"
              completed={Math.round(fraction * 10000) / 100}
              height="28px"
              labelAlignment="right"
              className="progressBarWrapper"
              labelClassName="progressBarCompletion"
              maxCompleted={100}
              width={"100%"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInsightsHeader;
